<template>
  <b-card
    class="revenue-trend-card mb-0 h-100"
    body-class="d-flex flex-column h-100"
  >
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <b-avatar
          variant="light-success"
          size="30"
          class="mr-50"
        >
          <b-spinner
            v-if="loading"
            small
            variant="primary"
          />
          <feather-icon
            v-else
            icon="DollarSignIcon"
            size="15"
          />
        </b-avatar>
        <small class="text-muted">{{ cardTitle }}</small>
      </div>

      <b-button-group size="sm">
        <b-button
          v-for="option in periodOptions"
          :key="option.value"
          :variant="period === option.value ? 'primary' : 'outline-secondary'"
          :disabled="loading"
          @click="changePeriod(option.value)"
        >
          {{ option.label }}
        </b-button>
      </b-button-group>
    </div>

    <div :class="{ 'opacity-50': loading }">
      <h2 class="font-weight-bolder mb-25">
        ฿{{ formatRevenue(revenueCurrent) }}
      </h2>
      <small :class="subtitleClass">{{ subtitle }}</small>
    </div>

    <vue-apex-charts
      type="area"
      height="160"
      class="mt-auto"
      :class="{ 'opacity-50': loading }"
      :options="chartOptions"
      :series="chartSeries"
    />
  </b-card>
</template>

<script>
import {
  BCard, BAvatar, BSpinner, BButtonGroup, BButton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import moment from 'moment'
import store from '@/store'
import { $themeColors } from '@themeConfig'
import { getUserData } from '@/auth/utils'
import closedealStoreModule from '@/views/close-deal/closedealStoreModule'

const CLOSE_DEAL_STORE_MODULE_NAME = 'app-close-deal'
const REVENUE_TREND_LINE_COLOR = '#b9b9c3'

// The API's own startDate/endDate filter turned out to be ignored entirely
// (same total came back for every range), so every bucket here is built from
// the one filter proven to work: whole-month `month`/`year` params. Month
// buckets use it directly; Year sums 12 months per year; Week fetches the
// spanned months' raw rows and buckets them by ISO week client-side.
const PERIOD_CONFIG = {
  week: { label: 'Week', count: 6, comparisonLabel: 'last week' },
  month: { label: 'Month', count: 6, comparisonLabel: 'last month' },
  year: { label: 'Year', count: 4, comparisonLabel: 'last year' },
}

export default {
  components: {
    BCard,
    BAvatar,
    BSpinner,
    BButtonGroup,
    BButton,
    VueApexCharts,
  },
  data() {
    return {
      loading: true,
      isIndividualScope: true,
      period: 'month',
      revenueCurrent: 0,
      revenuePrevious: 0,
      revenueHistory: [],
    }
  },
  computed: {
    periodOptions() {
      return Object.entries(PERIOD_CONFIG).map(([value, config]) => ({ value, label: config.label }))
    },
    cardTitle() {
      return this.isIndividualScope ? 'Your Sales Progress' : 'Revenue'
    },
    revenueDeltaPercent() {
      if (!this.revenuePrevious) return null
      return ((this.revenueCurrent - this.revenuePrevious) / this.revenuePrevious) * 100
    },
    subtitle() {
      const delta = this.revenueDeltaPercent
      const periodLabel = PERIOD_CONFIG[this.period].label.toLowerCase()
      if (delta === null) return this.isIndividualScope ? `Your progress this ${periodLabel}` : `This ${periodLabel}`
      return `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}% vs ${PERIOD_CONFIG[this.period].comparisonLabel}`
    },
    subtitleClass() {
      const delta = this.revenueDeltaPercent
      if (delta === null) return 'text-muted'
      return delta >= 0 ? 'text-success' : 'text-danger'
    },
    chartSeries() {
      return [{ name: 'Revenue', data: this.revenueHistory.map(point => point.value) }]
    },
    chartOptions() {
      const delta = this.revenueDeltaPercent
      const trendColor = delta !== null && delta < 0 ? $themeColors.danger : $themeColors.success

      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        grid: {
          borderColor: '#EBEBEB',
          strokeDashArray: 5,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: false } },
          padding: {
            top: 24, right: 30, bottom: 0, left: 30,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -10,
          formatter: value => `฿${this.formatRevenue(value)}`,
          style: {
            fontSize: '10px',
            colors: ['#6e6b7b'],
          },
          background: { enabled: false },
        },
        stroke: {
          width: 2,
          curve: 'smooth',
        },
        colors: [REVENUE_TREND_LINE_COLOR],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.15,
            opacityTo: 0,
            stops: [0, 100],
          },
        },
        markers: {
          size: 0,
          discrete: [
            {
              seriesIndex: 0,
              dataPointIndex: this.revenueHistory.length - 1,
              fillColor: trendColor,
              strokeColor: '#fff',
              strokeWidth: 2,
              size: 5,
            },
          ],
        },
        xaxis: {
          categories: this.revenueHistory.map(point => point.label),
          labels: {
            show: true,
            style: { fontSize: '11px' },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { show: false },
        tooltip: {
          x: { show: true },
          y: { formatter: value => `฿${this.formatRevenue(value)}` },
        },
      }
    },
  },
  created() {
    if (!store.hasModule(CLOSE_DEAL_STORE_MODULE_NAME)) store.registerModule(CLOSE_DEAL_STORE_MODULE_NAME, closedealStoreModule)

    this.fetchRevenue()
  },
  beforeDestroy() {
    if (store.hasModule(CLOSE_DEAL_STORE_MODULE_NAME)) store.unregisterModule(CLOSE_DEAL_STORE_MODULE_NAME)
  },
  methods: {
    changePeriod(period) {
      if (period === this.period) return
      this.period = period
      this.fetchRevenue()
    },
    fetchRevenue() {
      this.loading = true

      const userData = getUserData()

      // Matches the permission check in useCloseDealList.js so this sum agrees
      // with what the user actually sees after clicking through to the list.
      const canSeeAllDeals = userData && userData.ability && userData.ability.some(
        s => (s.action === 'all' && s.subject === 'CloseDeal') || (s.action === 'manage' && s.subject === 'all'),
      )
      const createdBy = !canSeeAllDeals && userData ? userData.id : ''
      this.isIndividualScope = !canSeeAllDeals

      let historyPromise
      if (this.period === 'year') {
        historyPromise = this.fetchYearlyHistory(userData, createdBy)
      } else if (this.period === 'week') {
        historyPromise = this.fetchWeeklyHistory(userData, createdBy)
      } else {
        historyPromise = this.fetchMonthlyHistory(userData, createdBy)
      }

      return historyPromise.then(history => {
        this.revenueHistory = history
        this.revenueCurrent = history[history.length - 1].value
        this.revenuePrevious = history[history.length - 2].value
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    fetchMonthSum(month, year, userData, createdBy) {
      return store.dispatch('app-close-deal/fetchData', {
        perpage: 1,
        offset: 0,
        code: '',
        type_id: userData ? userData.type_id : '',
        created_by: createdBy,
        month,
        year,
      }).then(response => Number((response.data.sums && response.data.sums.price) || 0))
    },
    fetchMonthRows(month, year, userData, createdBy) {
      return store.dispatch('app-close-deal/fetchData', {
        perpage: 1000,
        offset: 0,
        code: '',
        type_id: userData ? userData.type_id : '',
        created_by: createdBy,
        month,
        year,
      }).then(response => response.data.data || [])
    },
    fetchMonthlyHistory(userData, createdBy) {
      const { count } = PERIOD_CONFIG.month
      const months = Array.from({ length: count }, (_, i) => moment().subtract(count - 1 - i, 'months'))

      return Promise.all(
        months.map(m => this.fetchMonthSum(m.month() + 1, m.year(), userData, createdBy)
          .then(value => ({ label: m.format('MMM'), value }))),
      )
    },
    fetchYearlyHistory(userData, createdBy) {
      const { count } = PERIOD_CONFIG.year
      const years = Array.from({ length: count }, (_, i) => moment().subtract(count - 1 - i, 'years').year())

      return Promise.all(
        years.map(year => {
          const monthSums = Array.from(
            { length: 12 },
            (_, m) => this.fetchMonthSum(m + 1, year, userData, createdBy),
          )
          return Promise.all(monthSums).then(sums => ({
            label: String(year),
            value: sums.reduce((total, sum) => total + sum, 0),
          }))
        }),
      )
    },
    fetchWeeklyHistory(userData, createdBy) {
      const { count } = PERIOD_CONFIG.week
      const weeks = Array.from({ length: count }, (_, i) => {
        const start = moment().subtract(count - 1 - i, 'weeks').startOf('isoWeek')
        return { start, end: start.clone().endOf('isoWeek'), label: start.format('MMM D') }
      })

      // Fetch every calendar month the weeks span (via the proven month/year
      // filter) once, then bucket the returned rows by ISO week ourselves.
      const monthKeys = []
      const cursor = weeks[0].start.clone().startOf('month')
      const rangeEnd = weeks[weeks.length - 1].end.clone().startOf('month')
      while (cursor.isSameOrBefore(rangeEnd)) {
        monthKeys.push({ month: cursor.month() + 1, year: cursor.year() })
        cursor.add(1, 'month')
      }

      return Promise.all(
        monthKeys.map(({ month, year }) => this.fetchMonthRows(month, year, userData, createdBy)),
      ).then(rowsByMonth => [].concat(...rowsByMonth)).then(rows => weeks.map(week => {
        const value = rows
          .filter(deal => {
            const dealDate = moment(deal.date)
            return dealDate.isValid() && dealDate.isBetween(week.start, week.end, undefined, '[]')
          })
          .reduce((sum, deal) => sum + (Number(deal.price) || 0), 0)
        return { label: week.label, value }
      }))
    },
    formatRevenue(value) {
      return `${Math.round(value / 1000).toLocaleString('en-US')}k`
    },
  },
}
</script>

<style scoped>
.opacity-50 {
  opacity: 0.5;
  transition: opacity 0.15s ease-in-out;
}
</style>
